import { askText, askConfirm, bindDialog } from './cloud/dialog';
import { desktopEditingAllowed, renderDesktopRequired } from './cloud/device-policy';
import { WebKilnEditorAdapter } from './editor';
import { WebKilnEditorController } from './editor/webkiln-controller';
import { LocalProjectStorage } from './storage/project-storage';
import type { WebKilnProject } from './types';
import { WebKilnApiClient } from './cloud/api-client';
import { CloudProjectAdapter } from './cloud/cloud-project-adapter';
import { CloudEditorSync } from './cloud/cloud-editor-sync';
import { parseRoute, navigate } from './cloud/app-router';
import { renderAuthView } from './cloud/auth-view';
import { renderDashboard } from './cloud/dashboard-view';
import { renderOnboarding } from './cloud/onboarding-view';
import type {
  PagePermission,
  ProjectSyncRepository,
  Session,
  SiteProject,
  SiteRevision,
} from './cloud/contracts';
import { protectedRedirect } from './cloud/protected-route';
import { EditingExperienceController } from './editor/editing-experience';
import { DesignGuardianController } from './editor/design-guardian';
import { DynamicBindingController } from './editor/dynamic-binding';
import { InteractionEditorController } from './editor/interaction-editor';
import { renderPublishDialog } from './cloud/publish-dialog';

const cloud = new WebKilnApiClient();
const storage = new LocalProjectStorage();

async function boot(): Promise<void> {
  const route = parseRoute();
  if (route.kind !== 'local' && route.kind !== 'editor') {
    document.body.innerHTML =
      '<main class="cloud-app auth-app"><div role="status">Opening WebKiln…</div></main>';
  }
  if (route.kind !== 'editor') document.documentElement.removeAttribute('data-app-booting');
  if (route.kind === 'login' || route.kind === 'signup') {
    await renderAuthView(cloud, route.kind);
    return;
  }
  if (route.kind === 'review') {
    document.documentElement.dataset.sessionState = 'review';
    try {
      const reviewProject = await cloud.getReviewLinkProject(route.token);
      await bootEditor(reviewProject.site.id, reviewProject, false, {
        role: reviewProject.role ?? (reviewProject.mode === 'client' ? 'content_editor' : 'viewer'),
        contentOnly: reviewProject.mode === 'client',
        reviewToken: route.token,
      });
    } catch (error) {
      renderCloudFailure(error instanceof Error ? error.message : 'Could not open review link.');
    }
    return;
  }
  if (route.kind === 'dashboard' || route.kind === 'onboarding' || route.kind === 'editor') {
    if (!cloud.configured) {
      navigate('/login');
      return;
    }
    if (route.kind === 'dashboard' || route.kind === 'onboarding') {
      document.body.innerHTML =
        '<main class="cloud-app auth-app"><div class="auth-loading" role="status">Restoring your session…</div></main>';
    } else {
      document.documentElement.dataset.sessionState = 'loading';
    }
    try {
      const session = await cloud.getSession();
      const redirect = protectedRedirect(route, session);
      if (!session) {
        navigate(redirect ?? '/login');
        return;
      }
      document.documentElement.dataset.sessionState = 'authenticated';
      if (route.kind === 'dashboard') {
        await renderDashboard(cloud, storage, session, route.section, route.workspaceId);
        return;
      }
      if (route.kind === 'onboarding') {
        await renderOnboarding(cloud, session);
        return;
      }
      await bootCloudEditor(route.siteId, session, route.preview);
    } catch (error) {
      renderCloudFailure(error instanceof Error ? error.message : 'Could not open WebKiln.');
    }
    return;
  }
  await bootEditor();
}

async function bootEditor(
  siteId?: string,
  remote?: SiteProject,
  preview = false,
  options?: {
    role?: string;
    contentOnly?: boolean;
    reviewToken?: string;
    currentUserId?: string;
    pagePermissions?: PagePermission[];
    workspaceId?: string;
  },
): Promise<void> {
  if (!desktopEditingAllowed()) {
    renderDesktopRequired(siteId, preview);
    return;
  }
  const canvas = document.querySelector<HTMLElement>('#siteCanvas');
  if (!canvas) throw new Error('WebKiln canvas was not found');
  const project = storage.load();
  const adapter = new WebKilnEditorAdapter(canvas);
  const cloudProject = new CloudProjectAdapter(cloud);
  const recoveredLocal = Boolean(
    remote &&
    !remote.project.pages.length &&
    project.site.id === remote.site.id &&
    project.pages.length,
  );
  if (remote && !recoveredLocal) Object.assign(project, remote.project);
  window.WebKiln = {
    ...(window.WebKiln ?? {}),
    adapter,
    project,
    storage,
    cloud,
    cloudProject,
    cloudConfigured: cloud.configured,
  };
  window.WebKiln.recoverLegacy = () => storage.restoreLatestLegacy();
  try {
    await adapter.initialize();
    ensurePages(project, adapter.exportProjectData());
    const currentPage = project.pages.find((page) => page.id === project.currentPageId);
    if (currentPage?.projectData) adapter.loadProjectData(currentPage.projectData);
    const formDefinitions = siteId && remote ? await cloud.listForms(siteId).catch(() => []) : [];
    const editorController = new WebKilnEditorController(
      adapter,
      project,
      storage,
      formDefinitions,
      { ...options, cloud },
    );
    editorController.start();
    const switcher = document.querySelector<HTMLButtonElement>('.project-switcher');
    if (switcher) {
      switcher.innerHTML = `<span class="site-dot"></span>${escapeHtml(project.site.title)}<span>⌄</span>`;
      switcher.title = 'Back to your websites';
      switcher.addEventListener('click', () => navigate('/dashboard?section=websites'));
    }
    new EditingExperienceController(adapter, project, () =>
      editorController.markDirty('Responsive settings'),
    ).start();
    new DesignGuardianController(adapter, project, () =>
      editorController.markDirty('Design system'),
    ).start();
    new InteractionEditorController(adapter, project, () =>
      editorController.markDirty('Interaction settings'),
    ).start();
    if (siteId && remote)
      void new DynamicBindingController(
        adapter,
        cloud,
        remote.site.workspaceId,
        () => editorController.markDirty('Dynamic binding'),
        project,
      ).start();
    if (preview) editorController.setPreviewMode(true);
    editorController.syncInteractionRuntime();
    adapter.subscribe('update', () => {
      const badge = document.querySelector('#publishState');
      if (badge && badge.textContent !== 'Unpublished')
        badge.textContent = `${(badge as HTMLElement).dataset.visibility} · Unpublished changes`;
    });
    renderAccountMenu(cloud);
    renderCloudStatus(cloud, siteId);
    if (siteId && remote) {
      const syncRepo: ProjectSyncRepository = options?.reviewToken
        ? {
          getProject: async () => cloud.getReviewLinkProject(options.reviewToken!),
          saveProject: async (_id: string, req: Parameters<typeof cloud.saveProject>[1]) =>
            cloud.saveReviewLinkProject(options.reviewToken!, req),
        }
        : cloud;
      const sync = new CloudEditorSync(
        adapter,
        project,
        storage,
        syncRepo,
        siteId,
        remote.serverRevision,
        renderSyncState,
        (latest) => {
          const active = window.WebKiln?.cloudSync;
          if (active instanceof CloudEditorSync) renderConflict(active, latest);
        },
      );
      sync.start();
      if (recoveredLocal) {
        sync.captureAndQueue();
        showToast(
          'Local recovery restored',
          'Your saved browser copy has been restored and is being saved to the workspace.',
        );
      }
      if (!options?.contentOnly) {
        void renderRevisions(cloud, siteId, remote.serverRevision);
        void renderPublishPanel(cloud, siteId, remote.serverRevision);
        bindPublishButton(cloud, siteId, sync);
      }
      window.WebKiln.cloudSync = sync;
    }
    document.documentElement.dataset.editorEngine = 'webkiln';
    document.documentElement.removeAttribute('data-app-booting');
    document
      .querySelector('#saveState')
      ?.replaceChildren(document.createTextNode('✓ Saved · WebKiln'));
  } catch (error) {
    document.documentElement.dataset.editorEngine = 'webkiln-error';
    document.documentElement.removeAttribute('data-app-booting');
    document.querySelector('#saveState')?.replaceChildren(document.createTextNode('Editor error'));
    console.error(error);
  }
}

function bindPublishButton(client: WebKilnApiClient, siteId: string, sync: CloudEditorSync): void {
  document.querySelector('#publishBtn')?.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      openPublishDialog(client, siteId, sync);
    },
    true,
  );
}

function openPublishDialog(client: WebKilnApiClient, siteId: string, sync: CloudEditorSync): void {
  renderPublishDialog(client, siteId, sync, () => {
    renderSyncState('saved');
    void renderPublishPanel(client, siteId, sync.currentRevision);
  });
}

async function renderPublishPanel(
  client: WebKilnApiClient,
  siteId: string,
  _revision: number,
): Promise<void> {
  const panel = document.querySelector('#sitePanel');
  if (!panel) return;
  panel.querySelector('[data-publish-panel]')?.remove();
  const card = document.createElement('div');
  card.dataset.publishPanel = 'true';
  card.className = 'recovery-card publish-panel';
  card.innerHTML =
    '<strong>Public website</strong><small>Loading publish history…</small><div data-publish-history></div>';
  panel.append(card);
  try {
    const status = await client.getPublishStatus(siteId);
    let badge = document.querySelector<HTMLElement>('#publishState');
    if (!badge) {
      badge = document.createElement('span');
      badge.id = 'publishState';
      badge.className = 'status-chip';
      document.querySelector('.top-actions')?.prepend(badge);
    }
    const source = status.releases.find(
      (release) => release.id === status.currentReleaseId,
    )?.sourceRevision;
    badge.dataset.sourceRevision = String(source ?? -1);
    badge.dataset.visibility = status.published
      ? status.visibility === 'private'
        ? 'Private'
        : 'Public'
      : 'Unpublished';
    badge.textContent = `${badge.dataset.visibility}${status.published && source !== _revision ? ' · Unpublished changes' : ''}`;
    const history = card.querySelector<HTMLElement>('[data-publish-history]');
    const statusLine = status.published
      ? `${status.visibility === 'private' ? 'Private' : 'Public'} · ${status.releases.find((release) => release.id === status.currentReleaseId)?.sourceRevision === _revision ? 'Up to date' : 'Unpublished changes'} · ${new Date(status.publishedAt ?? '').toLocaleString()}`
      : 'Draft only · not public';
    card.querySelector('small')!.textContent = statusLine;
    if (history)
      history.innerHTML = status.releases.length
        ? status.releases
          .slice(0, 8)
          .map(
            (release) =>
              `<div class="revision-row"><span><b>Release ${release.releaseNumber}</b>${release.id === status.currentReleaseId ? ' · Live' : ''}<small>Source revision ${release.sourceRevision} · ${new Date(release.createdAt).toLocaleString()}</small></span>${release.id !== status.currentReleaseId ? `<button type="button" data-rollback="${escapeHtml(release.id)}">Rollback</button>` : ''}</div>`,
          )
          .join('')
        : '<small>No published versions yet.</small>';
    history?.querySelectorAll<HTMLButtonElement>('[data-rollback]').forEach((button) =>
      button.addEventListener('click', async () => {
        button.disabled = true;
        await client.rollbackPublishedSite(siteId, button.dataset.rollback ?? '');
        await renderPublishPanel(client, siteId, _revision);
        showToast('Published version restored', 'The selected immutable release is live again.');
      }),
    );
    if (status.published) {
      const unpublish = document.createElement('button');
      unpublish.type = 'button';
      unpublish.textContent = 'Unpublish';
      unpublish.addEventListener('click', async () => {
        await client.unpublishSite(siteId);
        await renderPublishPanel(client, siteId, _revision);
        showToast('Site unpublished', 'The public URL now returns not found.');
      });
      card.append(unpublish);
    }
  } catch {
    card.querySelector('small')!.textContent = 'Publish history unavailable.';
  }
}

async function bootCloudEditor(siteId: string, session: Session, preview = false): Promise<void> {
  const project = await cloud.getProject(siteId);
  await bootEditor(siteId, project, preview, {
    role: project.role,
    contentOnly: project.role === 'content_editor',
    currentUserId: session.user.id,
    pagePermissions: project.pagePermissions,
    workspaceId: project.site.workspaceId,
  });
}

function renderCloudStatus(client: WebKilnApiClient, siteId?: string): void {
  const topActions = document.querySelector('.top-actions');
  if (topActions && !topActions.querySelector('[data-cloud-sync]')) {
    const state = document.createElement('span');
    state.id = 'cloudSyncState';
    state.dataset.cloudSync = 'true';
    state.textContent = siteId
      ? 'Cloud ready'
      : client.configured
        ? 'Cloud available'
        : 'Local only';
    topActions.prepend(state);
  }
  const panel = document.querySelector('#sitePanel');
  if (!panel || panel.querySelector('[data-cloud-status]')) return;
  const card = document.createElement('div');
  card.dataset.cloudStatus = 'true';
  card.className = 'recovery-card cloud-status-card';
  card.innerHTML = client.configured
    ? `<strong>${siteId ? 'Cloud project connected' : 'Cloud workspace ready'}</strong><small>${siteId ? 'Changes are saved to the authenticated WebKiln workspace.' : 'Open a cloud site from the dashboard to sync this editor.'}</small><div><button type="button" data-open-dashboard>Dashboard</button>${siteId ? '<button type="button" data-save-local>Save local recovery copy</button>' : ''}</div>`
    : '<strong>Local-only mode</strong><small>No secure WebKiln backend is configured. Projects remain in this browser.</small>';
  panel.append(card);
  card
    .querySelector('[data-open-dashboard]')
    ?.addEventListener('click', () => navigate('/dashboard'));
  card.querySelector('[data-save-local]')?.addEventListener('click', () => {
    const project = window.WebKiln?.project;
    if (project) storage.save(project);
    showToast('Local recovery copy saved', 'Your current project is safe in this browser.');
  });
}

function renderAccountMenu(client: WebKilnApiClient): void {
  if (!client.configured) return;
  const actions = document.querySelector('.top-actions');
  if (!actions || actions.querySelector('[data-editor-account]')) return;
  const menu = document.createElement('details');
  menu.dataset.editorAccount = 'true';
  menu.className = 'editor-account';
  menu.innerHTML =
    '<summary aria-label="Open account menu">Account</summary><div class="account-popover"><span data-account-loading>Loading account…</span><a href="/dashboard">Dashboard</a><button type="button" data-editor-signout>Sign out</button></div>';
  actions.append(menu);
  void client
    .getSession()
    .then((session) => {
      const loading = menu.querySelector('[data-account-loading]');
      if (loading)
        loading.innerHTML = session
          ? `<strong>${escapeHtml(session.user.displayName)}</strong><small>${escapeHtml(session.user.email)}</small><em>● Cloud connected</em>`
          : '<strong>Signed out</strong>';
    })
    .catch(() => {
      const loading = menu.querySelector('[data-account-loading]');
      if (loading)
        loading.innerHTML = '<strong>Cloud unavailable</strong><small>Local editor mode</small>';
    });
  menu.querySelector('[data-editor-signout]')?.addEventListener('click', async () => {
    await client.signOut().catch(() => undefined);
    navigate('/login');
  });
}

function renderSyncState(state: 'saved' | 'saving' | 'offline' | 'failed' | 'conflict'): void {
  const element = document.querySelector<HTMLElement>('#cloudSyncState');
  if (!element) return;
  const labels = {
    saved: 'Cloud saved',
    saving: 'Saving to cloud…',
    offline: 'Offline · local copy safe',
    failed: 'Cloud save failed',
    conflict: 'Conflict · review needed',
  };
  element.textContent = labels[state];
  element.dataset.state = state;
}

function renderConflict(sync: CloudEditorSync, latest: SiteProject): void {
  sync.setConflict(latest);
  if (document.querySelector('[data-conflict-card]')) return;
  const panel = document.querySelector('#sitePanel');
  if (!panel) return;
  const card = document.createElement('div');
  card.dataset.conflictCard = 'true';
  card.className = 'recovery-card cloud-conflict-card';
  card.innerHTML =
    '<strong>Cloud version changed</strong><small>WebKiln did not overwrite newer data. Reload the cloud version or keep a local recovery copy.</small><div><button type="button" data-reload-cloud>Reload cloud version</button><button type="button" data-save-local>Save local recovery copy</button></div>';
  panel.append(card);
  card.querySelector('[data-reload-cloud]')?.addEventListener('click', async () => {
    const route = parseRoute();
    if (route.kind !== 'editor') return;
    const fresh = await cloud.getProject(route.siteId);
    sync.reloadRemote(fresh);
    card.remove();
  });
  card.querySelector('[data-save-local]')?.addEventListener('click', () => {
    const project = window.WebKiln?.project;
    if (project) storage.save(project);
    showToast('Local recovery copy saved', 'Resolve the conflict before saving again.');
  });
}

async function renderRevisions(
  client: WebKilnApiClient,
  siteId: string,
  current: number,
): Promise<void> {
  const panel = document.querySelector('#sitePanel');
  if (!panel) return;
  const card = document.createElement('div');
  card.className = 'recovery-card cloud-revisions-card';
  card.innerHTML =
    '<strong>Cloud revisions</strong><button type="button" data-create-checkpoint>Save checkpoint</button><small>Loading revision history…</small><div data-revision-list></div>';
  panel.append(card);
  card.querySelector('[data-create-checkpoint]')?.addEventListener('click', async () => {
    const name = await askText('Name this checkpoint', 'Before changes');
    if (!name?.trim()) return;
    try {
      const sync = window.WebKiln?.cloudSync;
      if (!(sync instanceof CloudEditorSync)) return;
      await sync.saveBeforePublish();
      const checkpoint = await client.createRevision(siteId, name.trim(), sync.currentRevision);
      sync.advanceRevision(checkpoint.revisionNumber);
      card.remove();
      await renderRevisions(client, siteId, sync.currentRevision);
    } catch (error) {
      showToast(
        'Checkpoint not saved',
        error instanceof Error ? error.message : 'Please try again.',
      );
    }
  });
  try {
    const revisions = await client.listRevisions(siteId);
    const list = card.querySelector<HTMLElement>('[data-revision-list]');
    if (list)
      list.innerHTML = revisions.length
        ? revisions
          .map(
            (revision: SiteRevision) =>
              `<div class="revision-row"><span><b>Revision ${revision.revisionNumber}</b>${revision.revisionNumber === current ? ' · Current' : ''}<small>${escapeHtml(revision.name)} · ${new Date(revision.createdAt).toLocaleString()}</small></span><button type="button" data-preview-revision="${escapeHtml(revision.id)}">Preview</button></div>`,
          )
          .join('')
        : '<small>No named cloud revisions yet.</small>';
    list?.querySelectorAll<HTMLButtonElement>('[data-preview-revision]').forEach(
      (button) =>
      (button.onclick = () => {
        const revision = revisions.find((item) => item.id === button.dataset.previewRevision);
        if (revision) openRevisionPreview(client, siteId, revision);
      }),
    );
  } catch {
    const status = card.querySelector('small');
    if (status) status.textContent = 'Revision history unavailable.';
  }
}

function openRevisionPreview(
  client: WebKilnApiClient,
  siteId: string,
  revision: SiteRevision,
): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = `<section class="setup-modal revision-preview-modal" role="dialog" aria-modal="true" aria-labelledby="revisionTitle"><div class="modal-head"><h2 id="revisionTitle">${escapeHtml(revision.name)} · Revision ${revision.revisionNumber}</h2><button type="button" class="mini-btn" data-close aria-label="Close preview">×</button></div><p>Review this saved version before restoring it. Your current work will be saved first.</p><iframe title="Saved revision preview" sandbox=""></iframe><p class="cloud-error" data-error hidden></p><div class="modal-actions"><button type="button" class="ghost-btn" data-close>Keep editing</button><button type="button" class="primary-btn" data-restore>Restore this version</button></div></section>`;
  const frame = backdrop.querySelector('iframe')!;
  frame.src = `/api/sites/${encodeURIComponent(siteId)}/revisions/${encodeURIComponent(revision.id)}/preview`;
  let snapshotPages: Array<{ id: string; name: string; deletedAt?: unknown }> = [];
  try {
    snapshotPages = JSON.parse(revision.snapshotData ?? '{}').pages ?? [];
  } catch {
    /* Old revisions may omit page metadata. */
  }
  if (snapshotPages.length) {
    const label = document.createElement('label');
    label.className = 'field';
    label.textContent = 'Page to preview';
    const select = document.createElement('select');
    snapshotPages
      .filter((page) => !page.deletedAt)
      .forEach((page) => {
        const option = document.createElement('option');
        option.value = page.id;
        option.textContent = page.name;
        select.append(option);
      });
    select.onchange = () => {
      frame.src = `/api/sites/${encodeURIComponent(siteId)}/revisions/${encodeURIComponent(revision.id)}/preview?page=${encodeURIComponent(select.value)}`;
    };
    label.append(select);
    frame.before(label);
    select.dispatchEvent(new Event('change'));
  }
  document.body.append(backdrop);
  let busy = false;
  const close = () => {
    if (busy) return;
    release();
    backdrop.remove();
  };
  const release = bindDialog(backdrop, close);
  backdrop
    .querySelectorAll('[data-close]')
    .forEach((button) => button.addEventListener('click', close));
  const restore = backdrop.querySelector<HTMLButtonElement>('[data-restore]')!;
  restore.onclick = async () => {
    if (
      !(await askConfirm(
        'Restore this version? A recovery checkpoint of your current work will be saved.',
      ))
    )
      return;
    busy = true;
    restore.disabled = true;
    restore.textContent = 'Restoring…';
    try {
      const sync = window.WebKiln?.cloudSync;
      if (!(sync instanceof CloudEditorSync)) throw new Error('Cloud connection unavailable');
      await sync.saveBeforePublish();
      await client.restoreRevision(siteId, revision.id, sync.currentRevision);
      window.location.reload();
    } catch (error) {
      const message = backdrop.querySelector<HTMLElement>('[data-error]')!;
      message.hidden = false;
      message.textContent =
        error instanceof Error ? error.message : 'Could not restore this version.';
      busy = false;
      restore.disabled = false;
      restore.textContent = 'Restore this version';
    }
  };
}

function renderCloudFailure(message: string): void {
  document.documentElement.removeAttribute('data-app-booting');
  document.body.innerHTML = `<main class="cloud-app auth-app"><section class="auth-card"><a class="cloud-brand" href="/">WEBKILN</a><p class="eyebrow">Something went wrong</p><h1>We couldn’t open this cloud project.</h1><p class="cloud-error">${escapeHtml(message)}</p><div class="modal-actions"><button class="ghost-btn" data-back-dashboard>Dashboard</button><button class="primary-btn" data-retry>Retry</button></div></section></main>`;
  document
    .querySelector('[data-back-dashboard]')
    ?.addEventListener('click', () => navigate('/dashboard'));
  document.querySelector('[data-retry]')?.addEventListener('click', () => window.location.reload());
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
      character,
  );
}
function showToast(title: string, detail: string): void {
  const toast = document.querySelector<HTMLElement>('#toast');
  if (toast) {
    toast.hidden = false;
    toast.querySelector('strong')!.textContent = title;
    toast.querySelector('small')!.textContent = detail;
    window.setTimeout(() => {
      toast.hidden = true;
    }, 3500);
  }
}

function ensurePages(project: WebKilnProject, initialData: unknown): void {
  if (project.pages.length) return;
  project.pages = [
    {
      id: 'home',
      name: 'Home',
      slug: '/',
      projectData: initialData,
      updatedAt: new Date().toISOString(),
      isHomepage: true,
      seo: { title: 'Home', description: '' },
      settings: { showInNavigation: true, passwordProtected: false },
    },
    {
      id: 'game-hosting',
      name: 'Game Hosting',
      slug: '/game-hosting',
      projectData: null,
      updatedAt: new Date().toISOString(),
      seo: { title: 'Game Hosting', description: '' },
      settings: { showInNavigation: true, passwordProtected: false },
    },
    {
      id: 'support',
      name: 'Support',
      slug: '/support',
      projectData: null,
      updatedAt: new Date().toISOString(),
      seo: { title: 'Support', description: '' },
      settings: { showInNavigation: true, passwordProtected: false },
    },
  ];
  project.currentPageId = 'home';
  project.homepagePageId = 'home';
}

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', () => void boot());
else void boot();
